import { useParams, Link, useNavigate } from 'react-router-dom'
import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react'
import { Database, FileText, Pencil, RefreshCw, Send, Trash2, Clock, GripVertical, Info, Plus, Check, X, Shuffle } from 'lucide-react'
import { useShare, useShares } from '../hooks/useShares'
import {
  useBreakdown, useEventFeed, useSummary, useTimeseries,
  type AnalyticsFilters,
} from '../hooks/useAnalytics'
import { rangeToFilters } from '../components/analytics/FilterBar'
import { StatTiles } from '../components/analytics/StatTiles'
import { SeriesChart } from '../components/analytics/SeriesChart'
import { BreakdownTable } from '../components/analytics/BreakdownTable'
import { useSeed } from '../hooks/useSeeds'
import { useSeedDataSummary } from '../hooks/useSeedData'
import { JsonViewer } from '@/components/JsonViewer'
import { D1FilteredTable } from '@/components/D1FilteredTable'
import { R2ScopedBrowser } from '@/components/R2ScopedBrowser'
import { DOSqlBrowser } from '@/components/DOSqlBrowser'
import { ResetProgressDialog } from '@/components/ResetProgressDialog'
import { WorkspaceEditor } from '@/components/WorkspaceEditor'
import { GfbundleApps } from '@/components/GfbundleApps'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { AnalyticsBreakdown, GeoEntry, TimelineBucket, MessageAttachment, ShareMessage, Workspace, WorkspaceThreadSummary, SeedVersion, SeedAppConfig } from '../types'
import { appLabel } from '@/lib/app-label'

/**
 * Resolve a relative capture URL to the absolute origin of the seed that
 * stored it. The relative path embeds the seed name, so we can construct
 * `https://{seed}.seeds.farmer1st.tech{path}` without a lookup.
 *
 * The container worker auth-gates the GET; the admin's `.seeds.farmer1st.tech`
 * JWT cookie (set when they log into landing) is what unlocks it. If the
 * admin hasn't logged into landing, the image will fail to load — log in once
 * to refresh the cookie.
 */
function attachmentUrl(att: MessageAttachment): string {
  const m = att.url.match(/^\/api\/captures\/captures\/([^/]+)\//)
  if (!m) return att.url
  const seed = m[1]
  const isLocal = location.hostname.endsWith('.local')
  const baseDomain = isLocal ? 'seeds.farmer1st.local' : 'seeds.farmer1st.tech'
  const protocol = isLocal ? 'http:' : 'https:'
  return `${protocol}//${seed}.${baseDomain}${att.url}`
}

/**
 * Per-workspace discussion. Admins see a tab strip (one tab per workspace),
 * each tab has an unread badge. Posting from the active tab targets that
 * workspace_id server-side.
 */
const APP_FILTER_ALL = '__all__'
const APP_FILTER_NONE = '__none__'

function WorkspaceDiscussion({ shareId, appsConfig, seedDisplayName }: {
  shareId: number
  appsConfig?: Record<string, SeedAppConfig>
  seedDisplayName?: string
}) {
  // Same labels as the allow-list and the container's app bar.
  const label = (k: string) => appLabel(k, appsConfig, seedDisplayName)
  const [threads, setThreads] = useState<WorkspaceThreadSummary[]>([])
  const [activeTab, setActiveTab] = useState<number | null>(null)
  const [messages, setMessages] = useState<ShareMessage[]>([])
  const [loadingMessages, setLoadingMessages] = useState(true)
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [appFilter, setAppFilter] = useState<string>(APP_FILTER_ALL)
  const [editingMsgId, setEditingMsgId] = useState<number | null>(null)
  const [tagError, setTagError] = useState<string | null>(null)
  const lastReadFiredRef = useRef<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const stickRef = useRef(true)
  const lastCountRef = useRef(0)

  function scrollToBottom(smooth: boolean) {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
  }
  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  // Reset scroll state on workspace tab switch.
  useLayoutEffect(() => {
    stickRef.current = true
    lastCountRef.current = 0
    requestAnimationFrame(() => scrollToBottom(false))
  }, [activeTab])

  // Auto-scroll on new messages only if user is already near the bottom.
  useEffect(() => {
    if (messages.length > lastCountRef.current && stickRef.current) {
      scrollToBottom(true)
    }
    lastCountRef.current = messages.length
  }, [messages.length])

  const refreshThreads = useCallback(async () => {
    const res = await fetch(`/api/shares/${shareId}/workspaces`)
    if (!res.ok) return
    const data = await res.json()
    const t: WorkspaceThreadSummary[] = data.workspaces || []
    setThreads(t)
    if (activeTab === null && t.length > 0) {
      setActiveTab(t[0].workspaceId)
    }
  }, [shareId, activeTab])

  useEffect(() => { refreshThreads() }, [refreshThreads])

  const loadMessages = useCallback(async (workspaceId: number) => {
    setLoadingMessages(true)
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/messages`)
      if (!res.ok) return
      const data = await res.json()
      setMessages(data.messages || [])
    } finally {
      setLoadingMessages(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === null) return
    loadMessages(activeTab)
  }, [activeTab, loadMessages])

  // Mark workspace as read once per active tab change, then refresh badges.
  useEffect(() => {
    if (activeTab === null) return
    if (lastReadFiredRef.current === activeTab) return
    lastReadFiredRef.current = activeTab
    fetch(`/api/workspaces/${activeTab}/read`, { method: 'POST' })
      .then(() => refreshThreads())
      .catch(() => {})
  }, [activeTab, refreshThreads])

  // Outgoing tag: if the filter is pinned to a specific app, the reply
  // inherits that app key. "All" and "No app" intentionally don't tag.
  const outgoingAppKey = appFilter !== APP_FILTER_ALL && appFilter !== APP_FILTER_NONE ? appFilter : undefined

  async function handleSend(e: React.FormEvent) {
    e.preventDefault()
    if (!newMessage.trim() || sending || activeTab === null) return
    setSending(true)
    try {
      const res = await fetch(`/api/workspaces/${activeTab}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newMessage.trim(), appKey: outgoingAppKey }),
      })
      if (res.ok) {
        setNewMessage('')
        await loadMessages(activeTab)
        await refreshThreads()
        stickRef.current = true
        requestAnimationFrame(() => scrollToBottom(true))
      }
    } finally {
      setSending(false)
    }
  }

  async function handleDelete(msgId: number) {
    if (!confirm('Delete this message?')) return
    if (activeTab === null) return
    await fetch(`/api/workspaces/${activeTab}/messages/${msgId}`, { method: 'DELETE' })
    await loadMessages(activeTab)
    await refreshThreads()
  }

  async function patchAppKey(msgId: number, newKey: string | null) {
    if (activeTab === null) return
    try {
      const res = await fetch(`/api/workspaces/${activeTab}/messages/${msgId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appKey: newKey }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setTagError(body.error || `Tag update failed (${res.status})`)
      } else {
        setTagError(null)
        await loadMessages(activeTab)
      }
    } catch (err) {
      setTagError(err instanceof Error ? err.message : String(err))
    } finally {
      setEditingMsgId(null)
    }
  }

  if (threads.length === 0) {
    return <p className="text-sm text-muted-foreground">No workspaces on this share yet — add one to start a discussion.</p>
  }

  const activeName = threads.find(t => t.workspaceId === activeTab)?.name ?? ''

  return (
    <div className="space-y-3">
      {/* Tab strip — one tab per workspace. */}
      <div className="flex flex-wrap gap-1 border-b">
        {threads.map(t => {
          const active = activeTab === t.workspaceId
          return (
            <button
              key={t.workspaceId}
              onClick={() => setActiveTab(t.workspaceId)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 -mb-[2px] transition-colors',
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              <span>{t.name}</span>
              {t.unreadCount > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                  {t.unreadCount}
                </Badge>
              )}
            </button>
          )
        })}
      </div>

      {(() => {
        const appKeysInUse = Array.from(new Set(messages.map(m => m.appKey).filter((k): k is string => !!k))).sort()
        const untaggedCount = messages.filter(m => !m.appKey).length
        const hasAnyTags = appKeysInUse.length > 0 || untaggedCount > 0
        const showFilter = hasAnyTags && messages.length > 1
        const visible = appFilter === APP_FILTER_ALL ? messages
          : appFilter === APP_FILTER_NONE ? messages.filter(m => !m.appKey)
          : messages.filter(m => m.appKey === appFilter)
        return (
          <>
            {showFilter && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Filter by app:</span>
                <select
                  value={appFilter}
                  onChange={e => setAppFilter(e.target.value)}
                  className="h-7 rounded border border-input bg-background px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value={APP_FILTER_ALL}>All ({messages.length})</option>
                  {appKeysInUse.map(k => (
                    <option key={k} value={k}>{label(k)} ({messages.filter(m => m.appKey === k).length})</option>
                  ))}
                  {untaggedCount > 0 && <option value={APP_FILTER_NONE}>No app ({untaggedCount})</option>}
                </select>
              </div>
            )}
      {loadingMessages ? (
        <Skeleton className="h-24 w-full" />
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {messages.length === 0 ? 'No messages yet in this workspace.' : 'No messages match this filter.'}
        </p>
      ) : (
        <div ref={scrollRef} onScroll={handleScroll} className="space-y-2 max-h-96 overflow-y-auto">
          {visible.map(msg => (
            <div key={msg.id} className="flex gap-3 group">
              <div className="flex-1 bg-muted/50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-sm font-medium">{msg.authorName}</span>
                  <Badge variant="outline" className="text-[10px] px-1 py-0">{msg.authorRole}</Badge>
                  {msg.appKey && (
                    editingMsgId === msg.id ? (
                      <select
                        autoFocus
                        defaultValue={msg.appKey}
                        onChange={e => patchAppKey(msg.id, e.target.value === '__none__' ? null : e.target.value)}
                        onBlur={() => setEditingMsgId(null)}
                        className="h-5 text-[10px] rounded border border-input bg-background px-1 focus:outline-none focus:ring-1 focus:ring-ring"
                      >
                        {appsConfig
                          ? Object.keys(appsConfig).map(k => (
                              <option key={k} value={k}>{label(k)}</option>
                            ))
                          : appKeysInUse.map(k => <option key={k} value={k}>{label(k)}</option>)
                        }
                        {appsConfig && !appsConfig[msg.appKey] && (
                          <option value={msg.appKey}>{label(msg.appKey)}</option>
                        )}
                        <option value="__none__">— No tag —</option>
                      </select>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingMsgId(msg.id)}
                        className="inline-flex items-center gap-0.5 hover:opacity-80 cursor-pointer"
                        title="Change app tag"
                      >
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 font-normal">
                          {label(msg.appKey)}
                        </Badge>
                        <Pencil className="h-2.5 w-2.5 text-muted-foreground" />
                      </button>
                    )
                  )}
                  <span className="text-xs text-muted-foreground">{new Date(msg.createdAt).toLocaleString()}</span>
                </div>
                {msg.content && (
                  <p className="text-sm text-foreground whitespace-pre-wrap">{msg.content}</p>
                )}
                {msg.attachments && msg.attachments.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {msg.attachments.map((att, i) => {
                      const href = attachmentUrl(att)
                      const isImage = !!att.mime && att.mime.startsWith('image/')
                      return isImage ? (
                        <a key={i} href={href} target="_blank" rel="noreferrer"
                           className="block max-w-[240px] rounded-md border border-border overflow-hidden hover:border-primary transition-colors">
                          <img src={href} alt={att.name || 'attachment'} className="block w-full h-auto" loading="lazy" />
                        </a>
                      ) : (
                        <a key={i} href={href} target="_blank" rel="noreferrer"
                           className="flex items-center gap-2 max-w-[280px] rounded-md border border-border bg-background hover:bg-muted hover:border-primary px-2 py-1.5 transition-colors">
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium truncate">{att.name || 'attachment'}</div>
                            <div className="text-[10px] text-muted-foreground">{att.bytes ? `${(att.bytes / 1024).toFixed(0)} KB` : ''}</div>
                          </div>
                        </a>
                      )
                    })}
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="xs"
                className="opacity-0 group-hover:opacity-100 text-destructive"
                onClick={() => handleDelete(msg.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
          </>
        )
      })()}

      {tagError && (
        <p className="text-xs text-destructive">{tagError}</p>
      )}
      <form onSubmit={handleSend} className="flex gap-2">
        <Input
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          placeholder={
            activeTab === null
              ? 'Select a workspace to reply'
              : outgoingAppKey
                ? `Reply in ${activeName} about ${outgoingAppKey}…`
                : `Reply in ${activeName}…`
          }
          className="flex-1"
          disabled={activeTab === null}
        />
        <Button type="submit" size="sm" disabled={sending || !newMessage.trim() || activeTab === null}>
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  )
}

function relativeTime(dateStr: string): string {
  const diff = new Date(dateStr).getTime() - Date.now()
  const absDiff = Math.abs(diff)
  const past = diff < 0
  if (absDiff < 3_600_000) {
    const m = Math.floor(absDiff / 60_000)
    return past ? `${m}m ago` : `in ${m}m`
  }
  if (absDiff < 86_400_000) {
    const h = Math.floor(absDiff / 3_600_000)
    return past ? `${h}h ago` : `in ${h}h`
  }
  const d = Math.floor(absDiff / 86_400_000)
  return past ? `${d}d ago` : `in ${d}d`
}

export function ShareDetailPage() {
  const { id } = useParams<{ id: string }>()
  const shareIdNum = id != null ? Number(id) : null
  const navigate = useNavigate()
  const { share, loading, error, refetch } = useShare(id!)
  const { updateShare, extendShare, deleteShare } = useShares()
  // Server-side aggregates scoped by the share id recorded on the event. The
  // page used to fetch up to 1,000 rows reconstructed from member emails and
  // add them up here, so every figure was a truncated sample of the wrong set.
  const shareFilters = useMemo<AnalyticsFilters>(() => {
    const { from, to } = rangeToFilters(90)
    // Matches the dashboard: a share page is about what the CLIENT did.
    return { from, to, shareId: shareIdNum ?? undefined, excludeInternal: true }
  }, [shareIdNum])
  const analyticsSummary = useSummary(shareFilters)
  const analyticsSeries = useTimeseries(shareFilters, 'day')
  const analyticsGeo = useBreakdown(shareFilters, 'country', undefined, 20)
  const analyticsApps = useBreakdown(shareFilters, 'app', undefined, 20)
  const recentEvents = useEventFeed(shareFilters, 25)
  const [actionError, setActionError] = useState('')
  const [resetDialogOpen, setResetDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const [versions, setVersions] = useState<SeedVersion[]>([])

  // Draft state — workspaces carry name + members + per-workspace apps.
  // `note` is stored as meta.note on the share; surfaced to clients as the share's description.
  const [committed, setCommitted] = useState<{ workspaces: Workspace[]; version: string; locale: string | null; note: string; name: string } | null>(null)
  const [draftWorkspaces, setDraftWorkspaces] = useState<Workspace[]>([])
  const [draftVersion, setDraftVersion] = useState<string>('latest')
  const [draftLocale, setDraftLocale] = useState<string | null>(null)
  const [draftNote, setDraftNote] = useState<string>('')
  const [draftName, setDraftName] = useState<string>('')
  const [editingName, setEditingName] = useState(false)
  const [nameError, setNameError] = useState<string>('')

  useEffect(() => {
    if (!share) return
    const versionVal = share.version ?? 'latest'
    const workspaces: Workspace[] = (share.workspaces || []).map(w => ({
      id: w.id,
      name: w.name,
      members: w.members.map(m => ({ recipient: m.recipient, kind: m.kind })),
      apps: w.apps,
      resetEnabled: w.resetEnabled ?? false,
    }))
    const snap = { workspaces, version: versionVal, locale: share.locale, note: share.note ?? '', name: share.name ?? '' }
    setCommitted(snap)
    setDraftWorkspaces(snap.workspaces)
    setDraftVersion(versionVal)
    setDraftLocale(snap.locale)
    setDraftNote(snap.note)
    setDraftName(snap.name)
    setEditingName(false)
    setNameError('')
  }, [share?.id])

  useEffect(() => {
    if (!share?.seed) return
    let cancelled = false
    fetch(`/api/seeds/${share.seed}/versions`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data?.versions) return
        setVersions(data.versions)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [share?.seed])

  const selectedVersion = draftVersion === 'latest'
    ? versions.find(v => v.isLatest) ?? versions.find(v => v.deployed || v.isCurrent)
    : versions.find(v => v.version === draftVersion)
  const versionLocales = selectedVersion?.locales || []

  useEffect(() => {
    if (!selectedVersion) return
    setDraftLocale(current => current === null || selectedVersion.locales.includes(current)
      ? current
      : null)
  }, [selectedVersion?.version, selectedVersion?.defaultLocale, selectedVersion?.locales.join('|')])

  const isDirty = useMemo(() => {
    if (!committed) return false
    return JSON.stringify(draftWorkspaces) !== JSON.stringify(committed.workspaces) ||
      draftVersion !== committed.version ||
      draftLocale !== committed.locale ||
      draftNote !== committed.note ||
      draftName.trim() !== committed.name
  }, [draftWorkspaces, draftVersion, draftLocale, draftNote, draftName, committed])

  const { seed } = useSeed(share?.seed || '')

  // Effective version drives the version-scoped D1 + R2 tabs:
  // pinned version on the share takes precedence, else the seed's current version.
  const effectiveVersion = share?.version || seed?.version || null

  const isExpired = share?.expired ?? false

  // --- Immediate actions ---

  async function handleExtend() {
    const days = prompt('Extend by how many days?', '14')
    if (!days) return
    const expiresAt = new Date(Date.now() + Number(days) * 86400000).toISOString()
    try {
      await extendShare(Number(id!), expiresAt)
      refetch()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to extend')
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this share? This cannot be undone.')) return
    try {
      await deleteShare(Number(id!))
      navigate('/shares')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  function handleReset() {
    setResetDialogOpen(true)
  }

  // --- Save / Cancel ---

  async function handleSave() {
    if (!share || !isDirty) return
    const trimmedName = draftName.trim()
    if (trimmedName !== committed!.name) {
      if (!trimmedName) {
        setNameError('Share name must not be empty')
        setEditingName(true)
        return
      }
      if (/\s/.test(trimmedName)) {
        setNameError('Share name must not contain whitespace')
        setEditingName(true)
        return
      }
    }
    try {
      setSaving(true)
      setActionError('')
      const updates: Record<string, unknown> = {}
      if (JSON.stringify(draftWorkspaces) !== JSON.stringify(committed!.workspaces)) {
        updates.workspaces = draftWorkspaces
      }
      if (draftVersion !== committed!.version) updates.version = draftVersion
      if (draftLocale !== committed!.locale) updates.locale = draftLocale
      if (draftNote !== committed!.note) updates.note = draftNote
      if (trimmedName !== committed!.name) updates.name = trimmedName

      await updateShare(share.id, updates as any)

      setCommitted({ workspaces: draftWorkspaces, version: draftVersion, locale: draftLocale, note: draftNote, name: trimmedName })
      setDraftName(trimmedName)
      setEditingName(false)
      setNameError('')
      refetch()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    navigate('/shares')
  }

  async function handleGenerateName() {
    const current = draftName.trim()
    const savedName = committed?.name ?? ''
    // Confirm whenever the saved name would be replaced — either the draft
    // is dirty, or the user cleared the saved name and is about to overwrite it.
    if (savedName && current !== savedName) {
      if (!confirm('Replace the current name with a generated one?')) return
    }
    try {
      const res = await fetch('/api/shares/_random-name')
      if (!res.ok) throw new Error('Failed to generate name')
      const data = await res.json() as { name: string }
      setDraftName(data.name)
      setNameError('')
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Failed to generate name')
    }
  }

  function handleNameCancel() {
    setDraftName(committed?.name ?? '')
    setEditingName(false)
    setNameError('')
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <Skeleton className="h-8 w-1/4 mb-4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    )
  }

  if (error || !share) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || 'Share not found'}</AlertDescription>
        </Alert>
      </div>
    )
  }

  const allSeedApps = (seed?.apps || []).filter(a => a !== 'worker')

  return (
    <div className="mx-auto max-w-7xl p-6">
      {/* Header with Cancel / Save */}
      <div className="flex items-center justify-between mb-6">
        <div className="min-w-0 flex-1 mr-4">
          <p className="text-sm font-medium text-muted-foreground mb-1">Share Name</p>
          {editingName ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={draftName}
                onChange={e => { setDraftName(e.target.value); if (nameError) setNameError('') }}
                placeholder="adjective-noun"
                pattern="\S+"
                className="h-9 max-w-xs text-base font-medium"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); setEditingName(false) }
                  if (e.key === 'Escape') { e.preventDefault(); handleNameCancel() }
                }}
              />
              <Button variant="outline" size="sm" onClick={handleGenerateName} title="Generate random name">
                <Shuffle className="h-4 w-4" />
                Generate
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditingName(false)} title="Done editing" aria-label="Done editing share name">
                <Check className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleNameCancel} title="Revert" aria-label="Revert share name to last saved">
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground truncate">{draftName || `Share #${share.id}`}</h1>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setEditingName(true)}
                title="Rename"
                aria-label="Rename share"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          {nameError && (
            <p className="text-xs text-destructive mt-1">{nameError}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          <Button variant="outline" size="sm" onClick={handleCancel}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={!isDirty || saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {actionError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      <p className="text-xs font-medium text-muted-foreground mb-3">Settings</p>
      <div className="grid gap-4 md:grid-cols-2 mb-8">
        {/* Configuration */}
        <Card>
          <div className="px-4 py-2.5">
            <p className="text-xs font-medium text-muted-foreground mb-2">Configuration</p>
            <div className="divide-y">
              <div className="py-2 flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-16 shrink-0">Status</span>
                <span className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
                  isExpired
                    ? 'bg-gray-100 text-gray-700'
                    : share.status === 'draft'
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-emerald-100 text-emerald-700',
                )}>
                  <span className={cn('h-1.5 w-1.5 rounded-full',
                    isExpired ? 'bg-gray-500' : share.status === 'draft' ? 'bg-gray-500' : 'bg-emerald-500'
                  )} />
                  {isExpired ? 'Expired' : share.status === 'draft' ? 'Draft' : 'Published'}
                </span>
              </div>
              <div className="py-2 flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-16 shrink-0">Seed</span>
                <Link to={`/seeds/${share.seed}`} className="text-xs font-medium text-primary hover:text-primary/80">{share.seed}</Link>
              </div>
              <div className="py-2 flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-16 shrink-0">Version</span>
                <select
                  value={draftVersion}
                  onChange={e => setDraftVersion(e.target.value)}
                  className="h-6 w-28 rounded border border-input bg-background px-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="latest">Latest</option>
                  {versions.filter(v => v.deployed || v.isCurrent).map(v => (
                    <option key={v.version} value={v.version}>
                      v{v.version}{v.isCurrent ? ' (current)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              {versionLocales.length > 1 && (
                <div className="py-2 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-32 shrink-0">Default content language</span>
                  <select
                    value={draftLocale || ''}
                    onChange={e => setDraftLocale(e.target.value || null)}
                    className="h-6 w-52 rounded border border-input bg-background px-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <option value="">Automatic — use visitor language</option>
                    {versionLocales.map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="py-2 flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-16 shrink-0">TTL</span>
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className={cn('text-xs font-medium', isExpired && 'text-destructive')}>{relativeTime(share.expiresAt)}</span>
                <Button size="sm" variant="outline" className="h-5 px-1.5 text-xs" onClick={handleExtend}>Extend</Button>
              </div>
              {isExpired && (
                <div className="py-2">
                  <p className="text-xs text-destructive">TTL expired. Extend to resume this share.</p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* The gfbundle of the version this share resolves to now (saved state, not the
            draft). Only for a Seed that names one: every other share keeps its one card. */}
        {share.gfbundle && (
          <Card>
            <div className="px-4 py-2.5">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Gfbundle
                {share.resolvedVersion && (
                  <span className="font-normal"> · of v{share.resolvedVersion}</span>
                )}
              </p>
              <div className="divide-y">
                <div className="py-2 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-16 shrink-0">Bundle</span>
                  <Link to="/gfbundles" className="text-xs font-mono font-medium text-primary hover:text-primary/80">
                    {share.gfbundle.version}
                  </Link>
                </div>
                <div className="py-2 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-16 shrink-0">Client</span>
                  <span className="text-xs font-mono">{share.gfbundle.client ?? '—'}</span>
                </div>
                <div className="py-2 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-16 shrink-0">Cell</span>
                  <span className="text-xs font-mono">{share.gfbundle.cell ?? '—'}</span>
                </div>
                <div className="py-2 flex items-start gap-2">
                  <span className="text-xs text-muted-foreground w-16 shrink-0 pt-0.5">Apps</span>
                  <GfbundleApps apps={share.gfbundle.apps} />
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Description — stored as meta.note (legacy field name); shown to clients on the landing seed list. */}
      <Card className="mb-8">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Description</CardTitle>
        </CardHeader>
        <CardContent>
          <Label htmlFor="share-description" className="sr-only">Description</Label>
          <Textarea
            id="share-description"
            value={draftNote}
            onChange={e => setDraftNote(e.target.value)}
            placeholder="Free-text description shown to recipients on their seed list."
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Workspaces — each workspace = isolated audience + isolated discussion. */}
      <Card className="mb-8">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Workspaces</CardTitle>
          <p className="text-xs text-muted-foreground">
            Each workspace is an isolated audience with its own discussion. Members of workspace A
            cannot see workspace B's messages. Add multiple workspaces to keep audiences apart
            on the same share. Removing a workspace deletes its discussion history.
          </p>
        </CardHeader>
        <CardContent>
          <WorkspaceEditor
            workspaces={draftWorkspaces}
            onChange={setDraftWorkspaces}
            availableApps={allSeedApps}
            appsConfig={seed?.appsConfig}
            restrictedApps={selectedVersion?.restrictedApps}
            seedDisplayName={seed?.displayName}
          />
        </CardContent>
      </Card>

      {/* Data section */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Data</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleReset} className="h-7">
                <RefreshCw className="h-3.5 w-3.5" />
                Reset
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="d1">
            <TabsList>
              <TabsTrigger value="d1">D1</TabsTrigger>
              <TabsTrigger value="r2">R2</TabsTrigger>
              <TabsTrigger value="do-sql">DO SQL</TabsTrigger>
            </TabsList>

            <TabsContent value="d1" className="mt-4 space-y-4">
              {shareIdNum !== null && (
                <>
                  <D1FilteredTable table="shares" filterCol="id" filterVal={shareIdNum} title="shares" />
                  {effectiveVersion ? (
                    <D1FilteredTable
                      table="share_data"
                      filters={[{ col: 'share_id', val: shareIdNum }, { col: 'version', val: effectiveVersion }]}
                      title={`share_data (v${effectiveVersion})`}
                    />
                  ) : (
                    <D1FilteredTable
                      table="share_data"
                      filterCol="share_id"
                      filterVal={shareIdNum}
                      title="share_data"
                    />
                  )}
                  <D1FilteredTable table="share_messages" filterCol="share_id" filterVal={shareIdNum} title="share_messages" />
                  <D1FilteredTable table="share_workspaces" filterCol="share_id" filterVal={shareIdNum} title="share_workspaces" />
                </>
              )}
            </TabsContent>

            <TabsContent value="r2" className="mt-4">
              {shareIdNum !== null && (
                effectiveVersion ? (
                  <R2ScopedBrowser
                    bucket="seeds"
                    basePrefix={`shares/${shareIdNum}/${effectiveVersion}/`}
                    lockedToBase
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No effective version resolved yet — share has no pinned version and the seed has no current version.
                  </p>
                )
              )}
            </TabsContent>

            <TabsContent value="do-sql" className="mt-4">
              {shareIdNum !== null && <DOSqlBrowser shareId={shareIdNum} />}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Discussion — per-workspace tabs */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-lg">Discussion</CardTitle>
        </CardHeader>
        <CardContent>
          <WorkspaceDiscussion
            shareId={share.id}
            appsConfig={seed?.appsConfig}
            seedDisplayName={seed?.displayName}
          />
        </CardContent>
      </Card>

      {/* Analytics */}
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-foreground">Analytics</h2>
        <Link
          to={`/analytics?shareId=${share.id}`}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Open in Analytics →
        </Link>
      </div>

      {analyticsSummary.data && analyticsSummary.data.events === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No analytics data for this share yet
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <StatTiles summary={analyticsSummary.data} loading={analyticsSummary.loading} />

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardContent className="p-5">
                <h3 className="mb-4 text-sm font-medium text-muted-foreground">Activity (90 days)</h3>
                <SeriesChart series={analyticsSeries.series} metric="interactions" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <h3 className="mb-4 text-sm font-medium text-muted-foreground">Apps</h3>
                <BreakdownTable rows={analyticsApps.rows} loading={analyticsApps.loading} keyHeader="App" />
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="border-b px-5 py-3">
              <h3 className="text-sm font-medium text-muted-foreground">Geography</h3>
            </CardHeader>
            <BreakdownTable rows={analyticsGeo.rows} loading={analyticsGeo.loading} keyHeader="Country" />
          </Card>

          <Card>
            <CardHeader className="border-b px-5 py-3">
              <h3 className="text-sm font-medium text-muted-foreground">Recent Events</h3>
            </CardHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Path</TableHead>
                  <TableHead>App</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentEvents.events.map(e => (
                  <TableRow key={e.id}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {new Date(e.ts).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={e.kind === 'api' ? 'outline' : 'secondary'}>{e.event_name || e.kind}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[20rem] truncate font-mono text-xs text-foreground">{e.path}</TableCell>
                    <TableCell className="text-muted-foreground">{e.app ?? '—'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {e.email === '_magic_link' ? 'Anonymous link' : e.email ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{e.status ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!recentEvents.exhausted && (
              <div className="border-t p-3 text-center">
                <Button variant="ghost" size="sm" disabled={recentEvents.loading} onClick={recentEvents.loadMore}>
                  {recentEvents.loading ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}

      <ResetProgressDialog
        open={resetDialogOpen}
        shareId={shareIdNum ?? 0}
        version={share?.version ?? null}
        onClose={() => setResetDialogOpen(false)}
        onComplete={() => refetch()}
      />
    </div>
  )
}
